import { Button, DataTable, Icon, Panel, type Column } from '@dayon/design-system';
import { formatDay } from '../format.ts';
import { sentence } from '../vocabulary.ts';
import type { ControlRow, RestrictionRow } from '../types.ts';

export function RestrictionsTab({
  controls,
  columns,
  restrictions,
  canRestrict,
  onApply,
}: {
  controls: ControlRow[];
  columns: Column<ControlRow>[];
  restrictions: RestrictionRow[];
  canRestrict: boolean;
  onApply: () => void;
}) {
  return (
    <Panel
      title="Account controls"
      actions={
        canRestrict ? (
          <Button small variant="primary" onClick={onApply}>
            Apply a control
          </Button>
        ) : null
      }
    >
      <div className="text-subtle" style={{ fontSize: '12.5px' }}>
        Every change writes an audit entry in the same transaction
      </div>

      <div
        className="notice notice-quiet"
        style={{ marginTop: 'var(--space-3)', marginBottom: 'var(--space-3)' }}
      >
        <Icon name="shield-check" />
        <div>
          {/* THREE COLUMN NAMES USED TO SIT IN THIS NOTICE, each inside the monospace
              wrapper this console uses to mean “this is a literal value” — which made
              them read as deliberate, and they were the one part of the sentence an
              operator could do nothing with. The row behind each state carries
              `active`, `lifted_at` and `expires_at`: a lift clears `active` and stamps
              `lifted_at`, and the route compares `expires_at` against now() when it
              decides whether a control is still in force. Those names belong here,
              where whoever reads this source needs them, and not on screen, because
              everything they were explaining is already visible in the State column:
              it reads On or Off, it says “until <day>” while a control is in force, and
              it reads “lifted <day>” or “expired <day>” once it is not. The distinction
              that had to survive the rewording is the last one — a lapse is nobody’s
              decision, so a lapsed control carries no lift date, and the word
              “lifted” against a row nobody lifted would credit an operator with a
              decision they never made. */}
          The states below are live, and so are the actions beside them: one applies a control and
          the other lifts it, by switching the state to Off and stamping the day of the lift, so the
          row itself keeps both the day it went on and the day it came off. A control with no row on
          file is genuinely Off rather than unknown, and one whose expiry date has passed also reads{' '}
          <strong>Off</strong> — that is the same test the withdrawal release applies before it pays
          out, so this table and the payout rail cannot disagree. An expiry is not a lift: it
          records no lift date, and the state reads expired rather than lifted, because nobody
          decided anything. Two rules bind: a <strong>self-exclusion set by the player</strong> is a
          full account lock that staff cannot lift — the platform itself refuses the lift, whatever
          asks for it, so the action reads &ldquo;not staff&rsquo;s to lift&rdquo; rather than failing
          when pressed — and <strong>raising a player-requested deposit limit</strong> carries a
          24-hour cooling-off, which nothing here schedules: the pending raise would be held on the
          player&rsquo;s own responsible-gaming limits, not on this table, so a raise applies the
          moment it is written. <strong>&ldquo;Notifies the player&rdquo; is not claimed</strong>{' '}
          {/* The KYC refusal on this profile stopped mailing on 23 Oct 2026 (players/routes.ts,
              "KYC review — CLOSED"); the identity queue's decision writes IDENTITY_RESULT. */}
          &mdash; and not for want of an outbox. The mail outbox takes a message at the moment a
          decision is saved and the platform sends it within the minute; an identity
          check&rsquo;s decision writes one today. Applying a control simply does not, and no
          template covers an operator-applied control, so nothing is sent and nothing is claimed.
        </div>
      </div>

      <DataTable
        caption="Account controls for this player, with the state on file and who set it"
        columns={columns}
        rows={controls}
        rowKey={(c) => c.kind}
      />

      {restrictions.some((r) => r.reason) && (
        <div className="stack" style={{ marginTop: 'var(--space-4)' }}>
          <div className="panel-title">Reasons on file</div>
          {restrictions
            .filter((r) => r.reason)
            .map((r) => (
              <div className="feed-row" key={r.id}>
                <span className="feed-time">{formatDay(r.effective_from)}</span>
                <span>
                  {sentence(r.kind)} — {sentence(r.reason ?? '')}
                  {/* Three states, not two: still on, taken off by a decision,
                      or simply run out. "Lifted" on a row that nobody lifted
                      would credit an operator with it. */}
                  {r.in_force
                    ? ''
                    : r.lifted_at
                      ? ` (lifted ${formatDay(r.lifted_at)})`
                      : r.expires_at
                        ? ` (expired ${formatDay(r.expires_at)})`
                        : ' (off)'}
                </span>
              </div>
            ))}
        </div>
      )}
    </Panel>
  );
}
